import { useDropdownState } from 'hooks/useDropdownState'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { useResponsive } from 'hooks/useResponsive'
import { Fragment, useCallback, useMemo } from 'react'

import * as Styled from './styled'
import { Field as FieldStyled } from '../../styled'

import { Text, SpinningLoader, Dropdown, SlideUp } from 'common-components'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { ITextData } from 'types/media'
import { COLORS } from 'consts/colors'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { editorSelectors } from 'store/features/editor/selectors'
import { ILibraryFontItem } from 'store/types/editor'
import { useSelector } from 'react-redux'

interface IFontDropdownProps {
  fontPostScriptName: string
  persistTextData: (patch: Partial<ITextData>) => void
  isFontLoading?: boolean
}

export const FontDropdown = (props: IFontDropdownProps) => {
  const { fontPostScriptName, persistTextData, isFontLoading = false } = props
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()
  const {
    isOpen,
    closeDropdown,
    toggleDropdown: rawToggleDropdown
  } = useDropdownState(openCallback, closeCallback)
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const useSlideUpFonts = isTablet
  const libraryFonts = useSelector(editorSelectors.libraryFonts)

  const toggleDropdown = useCallback(() => {
    if (isFontLoading) return
    rawToggleDropdown()
  }, [isFontLoading, rawToggleDropdown])

  const handleFontSelect = useCallback(
    (font: ILibraryFontItem) => {
      persistTextData({
        fontPostScriptName: font.postscriptName,
        fontPath: font.path
      })
      closeDropdown()
    },
    [persistTextData, closeDropdown]
  )

  const selectedFontId = useMemo(() => {
    if (!fontPostScriptName) return -1
    const match = libraryFonts.find(
      f => f.postscriptName === fontPostScriptName
    )
    return match?.id ?? -1
  }, [fontPostScriptName, libraryFonts])

  const selectedFont = useMemo(() => {
    if (selectedFontId === -1) return null
    return libraryFonts.find(f => f.id === selectedFontId) ?? null
  }, [selectedFontId, libraryFonts])

  const fontRows = useMemo(
    () =>
      libraryFonts.map(font => (
        <Styled.FontItemRow
          key={font.id}
          $isActive={font.id === selectedFontId}
          onClick={() => !isFontLoading && handleFontSelect(font)}
          style={{
            pointerEvents: isFontLoading ? 'none' : undefined
          }}
          data-test-id="text-overlay-font-dropdown-item"
        >
          <Styled.FontImg src={font.thumbnail} $dimmed={isFontLoading} />
        </Styled.FontItemRow>
      )),
    [selectedFontId, isFontLoading, handleFontSelect, libraryFonts]
  )

  const dropdownMainContent = <Styled.FontList>{fontRows}</Styled.FontList>

  const slideUpContent = (
    <Styled.MobileFontList>{fontRows}</Styled.MobileFontList>
  )

  return (
    <FieldStyled>
      <Styled.FontFieldHeader>
        <Text type="h2">{translate(T.TEXT_FONT_LABEL)}</Text>
      </Styled.FontFieldHeader>
      <Fragment>
        <Dropdown
          dropdownButtonTestId="text-overlay-font-dropdown-button"
          headerContent={
            selectedFont ? (
              <Styled.FontDropdownTriggerRow $isActive={false} $noHover>
                <Styled.FontHeaderLabel>
                  <Styled.FontImg
                    src={selectedFont.thumbnail}
                    $dimmed={isFontLoading}
                  />
                </Styled.FontHeaderLabel>
                <Styled.FontHeaderSpinnerSlot>
                  {isFontLoading ? (
                    <SpinningLoader
                      size={16}
                      wrapperSize={16}
                      loaderColor={COLORS.primaryDark}
                    />
                  ) : null}
                </Styled.FontHeaderSpinnerSlot>
              </Styled.FontDropdownTriggerRow>
            ) : (
              <Text type="subtitle" color="secondarySemiDark">
                {translate(T.SELECT_FONT_LABEL)}
              </Text>
            )
          }
          mainContent={useSlideUpFonts ? null : dropdownMainContent}
          isOpen={isOpen}
          toggleDropdown={toggleDropdown}
          closeDropdown={closeDropdown}
        />
        {useSlideUpFonts ? (
          <SlideUp isOpen={isOpen} onClose={closeDropdown}>
            {slideUpContent}
          </SlideUp>
        ) : null}
      </Fragment>
    </FieldStyled>
  )
}
